/**
 * Selectable virtual backgrounds.
 *
 * The three picker options are mutually exclusive, so they share one id space: `''` (no background),
 * `'blur'`, or a background's own id.
 *
 * `VIDEO_BACKGROUNDS` is a hard-coded, bundled list because this is a pilot for a single client
 * event. When backgrounds need to come from the backend, this array is the seam: replace it with a
 * fetched list of `{ id, name, imageUrl }` and nothing downstream changes — `applyBackground` takes
 * a URL, never an id, so it can't tell a bundled asset from a presigned CloudFront URL.
 *
 * Artwork requirements (see `public/assets/backgrounds/README.md`):
 *  - **Even width and height.** Odd dimensions make Agora's processor fail to build a WebGL texture
 *    (`texture bound to texture unit 2 is not renderable…`) and no background is applied.
 *  - **Key branding in the central 960x720.** Tiles vary in shape, so the left and right edges get
 *    cropped for many viewers.
 *  - **WebP.** The effect is gated to Chrome/Edge desktop anyway, so support is a given, and it's
 *    ~6x smaller than PNG on this kind of artwork (flat fields plus sharp-edged graphics) without
 *    JPEG's ringing around light-on-dark text.
 *
 * Served from `public/` rather than imported, so the URL is all anything downstream ever sees.
 */
import { uuid } from 'common/utils';

export type VideoBackground = {
  id: string;
  name: string;
  imageUrl: string;
};

export const NO_BACKGROUND_ID = '';
export const BLUR_BACKGROUND_ID = 'blur';

export const VIDEO_BACKGROUNDS: VideoBackground[] = [
  {
    id: 'service-now-ai-learning-days',
    name: 'AI Learning Days',
    imageUrl: '/assets/backgrounds/service-now-ai-learning-days.webp',
  },
];

export const findVideoBackground = (id: string): VideoBackground | undefined =>
  VIDEO_BACKGROUNDS.find((background) => background.id === id);

/**
 * Second tenant of the id space: images the user picked from their own device, stored in this browser
 * only (`webRTC/services/customBackgroundStorage`).
 *
 * A prefix on the existing id rather than a second field, because the whole pipeline — form field →
 * `settingsUpdated` → `userSlice` → `localStorage` → apply — already carries exactly one string, and
 * `isCustomBackgroundId` is synchronous, so it can be used even at slice-init time.
 */
export const CUSTOM_BACKGROUND_ID_PREFIX = 'custom:';

export const isCustomBackgroundId = (id: string) => id.startsWith(CUSTOM_BACKGROUND_ID_PREFIX);

export const createCustomBackgroundId = () => `${CUSTOM_BACKGROUND_ID_PREFIX}${uuid()}`;
